import { useTranslation } from 'next-i18next';
import { useState } from 'react';
import { useRouter } from 'next/router';
import { IArticle } from '@bridebook/toolbox/src/schemas/article/types';
import { Box, Col, Row } from '@bridebook/ui';
import BlockArticle from '@bridebook/web/app-basic/components/blocks/block-article';
import ArticlePagination from '@bridebook/web/app-shared/components/article-pagination/article-pagination';
import { useMarket } from '@bridebook/web/app-shared/lib/i18n/hooks/use-market';
import { UrlHelper } from '@bridebook/web/lib/url-helper';
import { toArticleCard } from '@bridebook/web/app-shared/lib/articles/to-article-card';
import componentStyles from './author-related-articles.style';
import { AUTHOR_ARTICLES_PER_PAGE } from '@bridebook/web/app-shared/lib/articles/constants';

interface IProps {
  authorName?: string;
  articles?: IArticle[];
  isMobile?: boolean;
  recordsFound?: number;
  currentPage?: number;
}

const AUTHOR_RELATED_ARTICLES = 6;

const AuthorRelatedArticles = ({
  authorName,
  articles,
  isMobile = false,
  recordsFound = 0,
  currentPage = 1,
}: IProps) => {
  const { t } = useTranslation('articles');
  const styles = componentStyles();
  const router = useRouter();
  const market = useMarket();

  const [viewAll, setViewAll] = useState(AUTHOR_RELATED_ARTICLES);

  const isViewAll = !recordsFound;
  const totalPages = Math.ceil(recordsFound / AUTHOR_ARTICLES_PER_PAGE);

  const handleClick = () => {
    setViewAll(articles ? articles?.length + 1 : 0);
  };

  const hrefBuilder = (index: number) => {
    const path = router.query.nameSurname
      ? UrlHelper.author.nameSurname(router.query.nameSurname as string)
      : UrlHelper.authorPage.id(Number(router.query.id));
    return `/${market.prefix}${path}?page=${index}`;
  };

  const handlePageChange = ({ selected }: { selected: number }) => {
    router.push(hrefBuilder(selected + 1));
  };

  return (
    <Box>
      <Box style={styles.headerContainer}>
        <Box style={styles.authorNameTitle}>{t('author.articles', { authorName })}</Box>
        {isViewAll && viewAll === AUTHOR_RELATED_ARTICLES && (
          <Box onClick={handleClick} style={styles.viewAllText}>
            {t('view.all')}
          </Box>
        )}
      </Box>
      <Box>
        {articles && (
          <Row rowGap={4} columnGap={4} data-name="articles-list">
            {(isViewAll ? articles.slice(0, viewAll) : articles).map((result) => (
              <Col xs={12} sm={6} md={6} lg={4} key={result.ID}>
                <BlockArticle article={toArticleCard(result)} />
              </Col>
            ))}
          </Row>
        )}
      </Box>
      {totalPages > 1 && !isViewAll && (
        <ArticlePagination
          items={totalPages}
          activePage={currentPage - 1}
          isMobile={isMobile}
          onSelect={handlePageChange}
          hrefBuilder={hrefBuilder}
        />
      )}
    </Box>
  );
};

export default AuthorRelatedArticles;
